/**
 * NFeFromQuotePage Component
 * 
 * Página para emitir NF-e modelo 55 a partir de um orçamento aprovado.
 * Converte o orçamento em venda automaticamente e emite a NF-e.
 * 
 * Funcionalidades:
 * - Exibir dados do orçamento
 * - Exibir itens do orçamento
 * - Seleção de formas de pagamento
 * - Opção de crediário
 * - Pré-visualização antes de emitir
 * - Emissão da NF-e
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, FileText, Eye, CheckCircle, AlertCircle,
  User, Package, CreditCard
} from 'lucide-react'
import { useToast } from '../../../../hooks/useToast'
import { nfeService, quoteService, customerService } from '../../../../services/erpApi'
import PaymentMethodModal from '../../../../components/ERP/PaymentMethodModal/PaymentMethodModal'
import Modal from '../../../../components/ERP/Modal/Modal'
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal'
import { Toast } from '../../../../components'
import './NFeFromQuotePage.css'

function NFeFromQuotePage() {
  const { quoteId } = useParams()
  const navigate = useNavigate()
  const { toast, showToast, hideToast } = useToast()
  
  const [quote, setQuote] = useState(null)
  const [customer, setCustomer] = useState(null)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [previewData, setPreviewData] = useState(null)
  const [loadingPreview] = useState(false) // eslint-disable-line no-unused-vars
  const [emitting, setEmitting] = useState(false)
  const [selectedPayments, setSelectedPayments] = useState([])
  const [createAccountReceivable, setCreateAccountReceivable] = useState(false)
  const [observation, setObservation] = useState('')
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  })

  useEffect(() => {
    if (quoteId) {
      loadQuoteData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId])

  const loadQuoteData = async () => {
    setLoading(true)
    try {
      // Carregar orçamento
      const quoteResponse = await quoteService.getById(quoteId)
      if (!quoteResponse.success || !quoteResponse.data) {
        showToast('Orçamento não encontrado', 'error')
        navigate('/erp/vendas/nfe')
        return
      }

      const quoteData = quoteResponse.data
      setQuote(quoteData)

      // Validar status
      if (quoteData.status !== 'approved') {
        showToast('Apenas orçamentos aprovados podem ser convertidos em NF-e', 'error')
        navigate('/erp/vendas/nfe')
        return
      }

      // Carregar cliente
      if (quoteData.customer_id) {
        try {
          const customerResponse = await customerService.getById(quoteData.customer_id)
          if (customerResponse.success && customerResponse.data) {
            setCustomer(customerResponse.data)
            
            // Validar CPF/CNPJ
            if (!customerResponse.data.cpf_cnpj) {
              showToast('Cliente não possui CPF/CNPJ cadastrado. É obrigatório para emitir NF-e.', 'error')
            }
          }
        } catch (error) {
          console.error('Erro ao carregar cliente:', error)
        }
      }

      // Carregar itens
      if (quoteData.items && quoteData.items.length > 0) {
        setItems(quoteData.items)
      }
    } catch (error) {
      console.error('Erro ao carregar dados do orçamento:', error)
      showToast('Erro ao carregar dados do orçamento', 'error')
      navigate('/erp/vendas/nfe')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenPaymentModal = () => {
    if (!customer || !customer.cpf_cnpj) {
      showToast('Cliente deve ter CPF/CNPJ cadastrado para emitir NF-e', 'error')
      return
    }
    setPaymentModalOpen(true)
  }

  const handlePaymentSelected = (paymentData) => {
    setSelectedPayments(paymentData)
    setPaymentModalOpen(false)
    
    // Verificar se há pagamento crediário
    const hasCrediario = paymentData.some(p => 
      p.paymentMethodName?.toLowerCase().includes('crediário') ||
      p.paymentMethodName?.toLowerCase().includes('crediario') ||
      p.name?.toLowerCase().includes('crediário') ||
      p.name?.toLowerCase().includes('crediario')
    )
    
    if (hasCrediario) {
      setCreateAccountReceivable(true)
    }
  }

  const handlePreview = async () => {
    if (!selectedPayments || selectedPayments.length === 0) {
      showToast('Selecione pelo menos uma forma de pagamento', 'error')
      return
    }

    // Para pré-visualização de orçamento, precisamos primeiro converter em venda
    // Por enquanto, vamos apenas mostrar os dados do orçamento
    setPreviewData({
      quote,
      customer,
      items,
      payments: selectedPayments,
      total: quote.final_amount
    })
    setPreviewModalOpen(true)
  }

  const handleEmit = () => {
    if (!selectedPayments || selectedPayments.length === 0) {
      showToast('Selecione pelo menos uma forma de pagamento', 'error')
      return
    }

    setConfirmModal({
      isOpen: true,
      title: 'Confirmar Emissão de NF-e',
      message: `Deseja emitir a NF-e a partir do orçamento #${quote.number}? O orçamento será convertido em venda automaticamente.`,
      onConfirm: async () => {
        await emitNFe()
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })
      }
    })
  }

  const emitNFe = async () => {
    setEmitting(true)
    try {
      const response = await nfeService.emitirFromQuote(quoteId, {
        payments: selectedPayments,
        createAccountReceivable: createAccountReceivable,
        observation: observation || null
      })

      if (response.success) {
        showToast('NF-e emitida com sucesso a partir do orçamento!', 'success')
        setTimeout(() => {
          navigate(`/erp/vendas/nfe/${response.data.invoice.id}`)
        }, 1500)
      } else {
        showToast(response.message || 'Erro ao emitir NF-e', 'error')
      }
    } catch (error) {
      console.error('Erro ao emitir NF-e:', error)
      showToast(error.response?.data?.message || 'Erro ao emitir NF-e', 'error')
    } finally {
      setEmitting(false)
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('pt-BR')
  }

  if (loading) {
    return (
      <div className="nfe-from-quote-page">
        <div className="loading">Carregando dados do orçamento...</div>
      </div>
    )
  }

  if (!quote) {
    return (
      <div className="nfe-from-quote-page">
        <div className="error-state">
          <AlertCircle size={48} />
          <p>Orçamento não encontrado</p>
          <button className="btn-primary" onClick={() => navigate('/erp/vendas/nfe')}>
            Voltar para Listagem
          </button>
        </div>
      </div>
    )
  }

  const totalAmount = quote.final_amount || quote.total_amount || 0

  return (
    <div className="nfe-from-quote-page">
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/erp/vendas/nfe')}>
          <ArrowLeft size={20} />
          Voltar
        </button>
        <h1>Emitir NF-e - Orçamento #{quote.number}</h1>
      </div>

      <div className="content-grid">
        {/* Dados do Orçamento */}
        <div className="card">
          <div className="card-header">
            <FileText size={20} />
            <h2>Dados do Orçamento</h2>
          </div>
          <div className="card-content">
            <div className="info-grid">
              <div className="info-item">
                <label>Número:</label>
                <span>#{quote.number}</span>
              </div>
              <div className="info-item">
                <label>Data:</label>
                <span>{formatDate(quote.quote_date)}</span>
              </div>
              <div className="info-item">
                <label>Validade:</label>
                <span>{formatDate(quote.validity_date)}</span>
              </div>
              <div className="info-item">
                <label>Valor Total:</label>
                <span className="total-amount">{formatCurrency(totalAmount)}</span>
              </div>
              {quote.client_notes && (
                <div className="info-item full-width">
                  <label>Observações:</label>
                  <span>{quote.client_notes}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dados do Cliente */}
        <div className="card">
          <div className="card-header">
            <User size={20} />
            <h2>Dados do Cliente</h2>
          </div>
          <div className="card-content">
            {customer ? (
              <>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Nome:</label>
                    <span>{customer.name}</span>
                  </div>
                  <div className="info-item">
                    <label>CPF/CNPJ:</label>
                    <span>{customer.cpf_cnpj || 'Não informado'}</span>
                    {!customer.cpf_cnpj && (
                      <span className="error-text">⚠️ Obrigatório para NF-e</span>
                    )}
                  </div>
                  {customer.email && (
                    <div className="info-item">
                      <label>E-mail:</label>
                      <span>{customer.email}</span>
                    </div>
                  )}
                  {customer.phone && (
                    <div className="info-item">
                      <label>Telefone:</label>
                      <span>{customer.phone}</span>
                    </div>
                  )}
                </div>
                {customer.address_street && (
                  <div className="address-section">
                    <label>Endereço:</label>
                    <p>
                      {customer.address_street}
                      {customer.address_number && `, ${customer.address_number}`}
                      {customer.address_neighborhood && ` - ${customer.address_neighborhood}`}
                      <br />
                      {customer.address_city && customer.address_city}
                      {customer.address_state && ` - ${customer.address_state}`}
                      {customer.address_zip_code && ` - CEP: ${customer.address_zip_code}`}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p className="no-data">Cliente não encontrado</p>
            )}
          </div>
        </div>

        {/* Itens do Orçamento */}
        <div className="card full-width">
          <div className="card-header">
            <Package size={20} />
            <h2>Itens do Orçamento</h2>
          </div>
          <div className="card-content">
            {items && items.length > 0 ? (
              <table className="items-table">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th>Quantidade</th>
                    <th>Preço Unit.</th>
                    <th>Desconto</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td>{item.product_name || `Produto #${item.product_id}`}</td>
                      <td>{parseFloat(item.quantity || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                      <td>{formatCurrency(item.unit_price)}</td>
                      <td>
                        {item.discount_amount > 0 
                          ? formatCurrency(item.discount_amount)
                          : item.discount_percent > 0
                          ? `${parseFloat(item.discount_percent).toFixed(2)}%`
                          : '-'
                        }
                      </td>
                      <td>{formatCurrency(item.total_price)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="4" className="text-right"><strong>Total:</strong></td>
                    <td><strong>{formatCurrency(totalAmount)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <p className="no-data">Nenhum item encontrado</p>
            )}
          </div>
        </div>

        {/* Formas de Pagamento */}
        <div className="card full-width">
          <div className="card-header">
            <CreditCard size={20} />
            <h2>Formas de Pagamento</h2>
          </div>
          <div className="card-content">
            {selectedPayments.length > 0 ? (
              <div className="selected-payments">
                <h3>Pagamentos Selecionados:</h3>
                <ul>
                  {selectedPayments.map((payment, index) => (
                    <li key={index}>
                      <strong>{payment.paymentMethodName || payment.name}:</strong>{' '}
                      {formatCurrency(payment.amount)}
                    </li>
                  ))}
                </ul>
                <div className="payment-total">
                  <strong>Total: {formatCurrency(
                    selectedPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
                  )}</strong>
                </div>
                <button
                  className="btn-secondary"
                  onClick={handleOpenPaymentModal}
                  style={{ marginTop: '1rem' }}
                >
                  Alterar Pagamentos
                </button>
              </div>
            ) : (
              <div className="no-payments">
                <p>Nenhuma forma de pagamento selecionada</p>
                <button className="btn-primary" onClick={handleOpenPaymentModal}>
                  Selecionar Formas de Pagamento
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Opções Adicionais */}
        <div className="card full-width">
          <div className="card-header">
            <CheckCircle size={20} />
            <h2>Opções Adicionais</h2>
          </div>
          <div className="card-content">
            <div className="options-list">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={createAccountReceivable}
                  onChange={(e) => setCreateAccountReceivable(e.target.checked)}
                />
                <span>Criar conta a receber (para pagamentos em crediário)</span>
              </label>
              <div className="form-field">
                <label>Observações (opcional):</label>
                <textarea
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                  placeholder="Observações adicionais para a NF-e..."
                  rows={3}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ações */}
      <div className="actions-bar">
        <button
          className="btn-secondary"
          onClick={() => navigate('/erp/vendas/nfe')}
        >
          Cancelar
        </button>
        <div className="actions-group">
          <button
            className="btn-secondary"
            onClick={handlePreview}
            disabled={selectedPayments.length === 0 || loadingPreview || emitting}
          >
            <Eye size={18} />
            Pré-visualizar
          </button>
          <button
            className="btn-primary"
            onClick={handleEmit}
            disabled={selectedPayments.length === 0 || emitting || !customer?.cpf_cnpj}
          >
            {emitting ? 'Emitindo...' : 'Emitir NF-e'}
          </button>
        </div>
      </div>

      {/* Modal de Pagamento */}
      <PaymentMethodModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onConfirm={handlePaymentSelected}
        totalAmount={totalAmount}
        required={true}
      />

      {/* Modal de Pré-visualização */}
      <Modal
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        title="Pré-visualização da NF-e"
        size="large"
      >
        {previewData && (
          <div className="preview-content">
            <div className="preview-section">
              <h3>Dados do Orçamento</h3>
              <div className="preview-summary">
                <p><strong>Orçamento:</strong> #{previewData.quote?.number}</p>
                <p><strong>Cliente:</strong> {previewData.customer?.name}</p>
                <p><strong>Total:</strong> {formatCurrency(previewData.total)}</p>
                <p><strong>Itens:</strong> {previewData.items?.length || 0}</p>
                <p><strong>Pagamentos:</strong> {previewData.payments?.length || 0}</p>
              </div>
            </div>
            <div className="preview-note">
              <AlertCircle size={20} />
              <p>O orçamento será convertido em venda automaticamente ao emitir a NF-e.</p>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Confirmação */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
        duration={4000}
      />
    </div>
  )
}

export default NFeFromQuotePage
